// ADR 0023 Sprint 7 Wave B — Per-tenant audit API.
//
// Separate from features/audit/api.ts (which covers the global /audit endpoint).
// This module is scoped to /tenants/{tenant_id}/audit — tenant-level audit events
// for the TenantAuditPage in the platform management screens.
//
// The query key prefix is ['platform', 'tenant-audit', ...] to avoid polluting
// the global ['audit', ...] cache.
//
// Security:
//   - before_state / after_state may contain settings payloads (AF-007).
//     They are intentionally typed as optional (Wave A deferred them as "M2").
//   - Never log response bodies.
//
// tenant_id is the numeric BIGINT PK from TenantSummary.id — same convention
// as tenant_settings_handler.go.
import { apiJson } from '@/api/client'
import { targetTenantHeader } from './context'
import type { TenantAuditListResponse } from './types'

export interface TenantAuditFilter {
  page?: number
  pageSize?: number
}

/**
 * GET /tenants/{tenant_id}/audit
 * Returns paginated audit events for a tenant.
 *
 * page_size is capped at 500 by the backend.
 * Note: before_state / after_state are NOT returned by the backend in Wave A
 * (deferred as M2). The TenantAuditEvent type has them as optional to be
 * forward-compatible.
 *
 * tenant_id is the numeric local PK from TenantSummary.id.
 */
export async function listTenantAudit(
  tenantId: number,
  filter: TenantAuditFilter = {},
  targetTenantId?: string | null,
): Promise<TenantAuditListResponse> {
  const params = new URLSearchParams()
  if (filter.page) params.set('page', String(filter.page))
  if (filter.pageSize) params.set('page_size', String(Math.min(filter.pageSize, 500)))
  const qs = params.toString()
  return apiJson<TenantAuditListResponse>(
    `/tenants/${tenantId}/audit${qs ? `?${qs}` : ''}`,
    { headers: targetTenantHeader(targetTenantId) },
  )
}
